import axios from 'axios';
import api from './api';
import type { ShareSettings, ShareStatus, ControlValues } from '../types/dashboard';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.zamino.com';

const publicApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

const SHARE_SESSION_KEY = 'bizguard_share_session';

function getSessionToken(shareToken: string): string | null {
  try {
    const data = JSON.parse(sessionStorage.getItem(SHARE_SESSION_KEY) || '{}');
    return data[shareToken] ?? null;
  } catch {
    return null;
  }
}

function setSessionToken(shareToken: string, token: string): void {
  try {
    const data = JSON.parse(sessionStorage.getItem(SHARE_SESSION_KEY) || '{}');
    data[shareToken] = token;
    sessionStorage.setItem(SHARE_SESSION_KEY, JSON.stringify(data));
  } catch {
    sessionStorage.setItem(SHARE_SESSION_KEY, JSON.stringify({ [shareToken]: token }));
  }
}

function clearSessionToken(shareToken: string): void {
  try {
    const data = JSON.parse(sessionStorage.getItem(SHARE_SESSION_KEY) || '{}');
    delete data[shareToken];
    sessionStorage.setItem(SHARE_SESSION_KEY, JSON.stringify(data));
  } catch {
    // Ignore
  }
}

function buildAuthHeaders(shareToken: string): Record<string, string> {
  const token = getSessionToken(shareToken);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// =============================================================================
// Authenticated (owner) API — uses existing api client with Bearer token
// =============================================================================

export const shareOwnerApi = {
  getShareStatus: async (dashboardId: string): Promise<ShareStatus> => {
    const response = await api.get(`/api/dashboards/${dashboardId}/share`);
    return response.data.data;
  },

  enableSharing: async (
    dashboardId: string,
    settings?: Partial<ShareSettings>
  ): Promise<ShareStatus> => {
    const response = await api.post(`/api/dashboards/${dashboardId}/share/enable`, { settings });
    return response.data.data;
  },

  disableSharing: async (dashboardId: string): Promise<void> => {
    await api.post(`/api/dashboards/${dashboardId}/share/disable`);
  },

  revokeLink: async (dashboardId: string): Promise<ShareStatus> => {
    const response = await api.post(`/api/dashboards/${dashboardId}/share/revoke`);
    return response.data.data;
  },

  updateShareSettings: async (
    dashboardId: string,
    settings: Partial<ShareSettings>
  ): Promise<ShareSettings> => {
    const response = await api.patch(`/api/dashboards/${dashboardId}/share/settings`, settings);
    return response.data.data;
  },

  setPin: async (dashboardId: string, pin: string): Promise<void> => {
    await api.post(`/api/dashboards/${dashboardId}/share/pin`, { pin });
  },

  removePin: async (dashboardId: string): Promise<void> => {
    await api.post(`/api/dashboards/${dashboardId}/share/pin`, { pin: null });
  },
};

// =============================================================================
// Public (viewer) API — no auth interceptor, uses share token
// =============================================================================

export interface ShareMeta {
  pin_required: boolean;
  dashboard_name: string;
  expired: boolean;
}

export interface SharedDashboardData {
  dashboard: {
    id: string;
    name: string;
    description?: string | null;
    data_mode: string;
    refresh_interval?: number | null;
    theme: string;
    layout_columns: number;
    share_settings: ShareSettings;
  };
  blocks: Array<Record<string, unknown>>;
  controls: Array<Record<string, unknown>>;
  block_data: Record<string, Record<string, unknown>[]>;
}

export const shareViewerApi = {
  getMeta: async (shareToken: string): Promise<ShareMeta> => {
    const response = await publicApi.get(`/api/share/${shareToken}`);
    return response.data.data;
  },

  verifyPin: async (shareToken: string, pin: string): Promise<string> => {
    const response = await publicApi.post(`/api/share/${shareToken}/verify`, { pin });
    const sessionToken = response.data.session_token;
    setSessionToken(shareToken, sessionToken);
    return sessionToken;
  },

  getDashboard: async (shareToken: string): Promise<SharedDashboardData> => {
    const response = await publicApi.get(`/api/share/${shareToken}/dashboard`, {
      headers: buildAuthHeaders(shareToken),
    });
    return response.data.data;
  },

  refreshData: async (
    shareToken: string,
    controlValues?: ControlValues
  ): Promise<Record<string, Record<string, unknown>[]>> => {
    let url = `/api/share/${shareToken}/data`;
    if (controlValues) {
      const encoded = encodeURIComponent(JSON.stringify(controlValues));
      url += `?controlValues=${encoded}`;
    }
    const response = await publicApi.get(url, {
      headers: buildAuthHeaders(shareToken),
    });
    return response.data.data;
  },

  getSessionToken,
  setSessionToken,
  clearSessionToken,
};
